import { useCallback } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { Redirect, useHistory, useParams } from 'react-router-dom';

import { invoiceDetails, lineAmount, PAYMENT_METHOD_LABELS } from './format';
import { HURDLR_SERIF, SectionHeading } from './fields';
import InvoicePageLayout from './InvoicePageLayout';
import InvoicePreview from './InvoicePreview';
import InvoiceTotals from './InvoiceTotals';
import StatusChip from './StatusChip';
import { Client, Invoice, Sender } from './types';

import CheckSmallIcon from 'modules/common/components/Icons/CheckSmallIcon';

interface InvoiceDetailProps {
    invoices: Invoice[];
    clients: Client[];
    sender: Sender;
}

/** An existing invoice, opened from the list — the "show me an invoice" moment in a demo call. */
export default function InvoiceDetail({ invoices, clients, sender }: Readonly<InvoiceDetailProps>) {
    const history = useHistory();
    const { invoiceId } = useParams<{ invoiceId: string }>();
    const handleBack = useCallback(() => history.push('/bookkeeping/invoicing'), [history]);

    const invoice = invoices.find((inv) => inv.id === invoiceId);
    if (!invoice) {
        return <Redirect to="/bookkeeping/invoicing" />;
    }

    const { clientEmail, items, discount, discountLabel, message, paymentMethods } = invoiceDetails(invoice, clients);
    const subtotal = items.reduce((sum, item) => sum + lineAmount(item), 0);

    return (
        <InvoicePageLayout
            title={`Invoice ${invoice.number}`}
            onBack={handleBack}
            main={
                <InvoicePreview
                    sender={sender}
                    number={invoice.number}
                    date={invoice.date}
                    due={invoice.due}
                    clientName={invoice.client}
                    clientEmail={clientEmail}
                    items={items}
                    discountAmount={discount}
                    total={invoice.amount}
                    message={message}
                />
            }
            side={
                <>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                        <Typography variant="h3" sx={{ fontFamily: HURDLR_SERIF, fontWeight: 400 }}>
                            Invoice {invoice.number}
                        </Typography>
                        <StatusChip status={invoice.status} />
                    </Stack>
                    <InvoiceTotals subtotal={subtotal} discountLabel={discountLabel} total={invoice.amount} />

                    <Box sx={{ mt: 6 }}>
                        <SectionHeading>Payment Processing</SectionHeading>
                        {paymentMethods.map((method) => (
                            <Stack
                                key={method}
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                sx={{ py: 1.5, borderBottom: 1, borderColor: 'cement.300' }}>
                                <CheckSmallIcon fontSize="small" color="success" />
                                <Typography variant="body2">{PAYMENT_METHOD_LABELS[method]}</Typography>
                            </Stack>
                        ))}
                    </Box>
                </>
            }
        />
    );
}
